import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useProfile } from '@/lib/providers';
import { AdminRole } from '@/types/custom';

interface AdminPreviewContextType {
  isPreviewMode: boolean;
  togglePreviewMode: () => void;
  /** Returns 'hoofdhartuser' when admin is in preview mode, otherwise the real role */
  effectiveRole: string | undefined;
}

const AdminPreviewContext = createContext<AdminPreviewContextType | undefined>(undefined);

export function AdminPreviewProvider({ children }: { children: ReactNode }) {
  const { profile } = useProfile();
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  const isAdmin = profile?.role === AdminRole;
  const realRole = profile?.role;

  const effectiveRole = isAdmin && isPreviewMode ? 'hoofdhartuser' : realRole;

  const togglePreviewMode = () => {
    if (!isAdmin) return;
    setIsPreviewMode((prev) => !prev);
  };

  // Reset preview mode when profile changes (e.g. logout)
  useEffect(() => {
    if (!isAdmin) setIsPreviewMode(false);
  }, [isAdmin]);

  // Manage hoofdhartuser class on html element
  useEffect(() => {
    if (effectiveRole === 'hoofdhartuser') {
      document.documentElement.classList.add('hoofdhartuser');
    } else {
      document.documentElement.classList.remove('hoofdhartuser');
    }
  }, [effectiveRole]);

  return (
    <AdminPreviewContext.Provider value={{ isPreviewMode, togglePreviewMode, effectiveRole }}>
      {children}
    </AdminPreviewContext.Provider>
  );
}

export function useAdminPreview() {
  const context = useContext(AdminPreviewContext);
  if (!context) {
    return { isPreviewMode: false, togglePreviewMode: () => {}, effectiveRole: undefined };
  }
  return context;
}
